'use client';

import { memo } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import NextButton from '@/components/Button/NextButton';
import Input from '@/components/Input/Input';
import { FormData } from '@/types/form';
import { fieldRules } from '@/utils/form';
import generalStyles from '../FormBody.module.scss';
import useModal from '@/hooks/useModal';
import styles from './StepFour.module.scss';

interface Props {
  register?: UseFormRegister<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  trigger?: UseFormTrigger<FormData>;
  setValue?: UseFormSetValue<FormData>;
}

const AnnualIncomeForm = ({
  register,
  handleSubmit,
  control,
  trigger,
  setValue,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = await trigger?.();
    if (isValid) {
      await handleSubmit?.();
      closeModal();
    }
  };

  const handleSumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value.replace(/\D/g, '');
    const cursorPosition = input.selectionStart || 0;

    const parsedValue = parseInt(rawValue, 10);
    const formattedValue = Number.isNaN(parsedValue)
      ? ''
      : parsedValue.toLocaleString('en-US');

    const addedChars = formattedValue.length - input.value.length;

    e.target.value = formattedValue;
    setValue?.('annual_income', formattedValue);

    setTimeout(() => {
      const newCursorPosition = cursorPosition + addedChars;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      {register && (
        <Input
          id="annual_income"
          label="Annual income before taxes"
          placeholder="Your annual income"
          type="text"
          inputMode="numeric"
          register={register('annual_income', {
            ...fieldRules.annual_income,
            onChange: handleSumChange,
          })}
          error={errors?.annual_income}
        />
      )}
      <NextButton
        buttonType="submit"
        type="button"
        text="Apply changes"
        styles={`${generalStyles.button} ${generalStyles.smallButton}`}
      />
    </form>
  );
};

export default memo(AnnualIncomeForm);
